import React from 'react';
import {
  withStyles,
  Modal,
  Typography,
  Box,
} from '@material-ui/core';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import CreateIcon from '@material-ui/icons/Create';
import CloseIcon from '@material-ui/icons/Close';
import PF2MAutocomplete from '~/components/PF2MAutocomplete';
import {
  Card,
  CardBody,
  CardHeader,
  CardIcon,
} from '~/components/Card';
import {
  bulkInsertDynamic,
  getDynamicConfiguration,
  getLoadAllocations,
  updateIsTruckAllocationLoading,
} from '~/store/dispatch/actions';
import { CheckCircleOutlineOutlined, DeleteOutlined, HelpOutline } from '@material-ui/icons';
import styles from './styles';
import PF2MNumericField from '~/components/PF2MNumericField';
import PF2MGenericButton from '~/components/PF2MGenericButton';
import PF2MSearchInput from '~/components/PF2MSearchInput';
import PF2MTooltip from '~/components/PF2MTooltip';
import useTabletConfiguration from '~/services/tabletConfiguration';


const BulkDynamicAllocationModal = ({
  isOpen,
  close,
  classes,
  container,
}) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const loadAllocations = useSelector(state => state.dispatch.loadAllocations);
  const equipments = useSelector(state => state.nref.equipments);
  const loadAllocationWithEquipName = loadAllocations.filter(la => la.id).map(
    (la) => {
      const foundEquip = equipments?.find(eq => eq.id === la.equip_id);
      return { ...la, equipName: foundEquip?.name };
    },
  );
  const laOptions = loadAllocationWithEquipName.map(la => ({ value: la.id, label: la.equipName }));
  const { value: dispatchRealTime } = useTabletConfiguration('dispatch_real_time');
  const disabledRealTime = dispatchRealTime === '0';

  const emptyValues = ['', 0, '0', null, undefined];
  const initialValues = {
    equipments: [],
    group_name: '',
    max_allocation_capacity: 0,
    max_load_queue_time: 0,
    mean_queue_time_range: 0,
    min_allocation_capacity: 0,
  };

  const handleClose = () => {
    close();
  };

  const handleOnSubmit = (values) => {
    dispatch(updateIsTruckAllocationLoading(true));
    const loadAllocationIds = values.equipments.map(eq => eq.value);

    const loadAfterRequestList = disabledRealTime
      ? [getLoadAllocations, getDynamicConfiguration]
      : [getDynamicConfiguration];

    dispatch(bulkInsertDynamic({
      payload: { ...values, equipments: loadAllocationIds },
      loadAfterRequest: loadAfterRequestList,
    }));
    handleClose();
  };

  const searchFieldClasses = {
    box: classes?.box,
    labelTypeSelector: classes?.labelTypeSelector,
    root: classes?.root,
  };

  const renderLabelWithInfo = ({ description, tooltipText }) => (
    <>
      <Box className={classes.labelWithInfo}>
        <Typography className={classes.formDescription}>{description}</Typography>
        <PF2MTooltip
          title={tooltipText}
          PopperProps={{ disablePortal: true }}
        >
          <HelpOutline fontSize="small" style={{ marginLeft: 5 }} />
        </PF2MTooltip>
      </Box>
    </>
  );

  return (
    <Modal
      open={isOpen}
      onClose={(event, reason) => reason === 'backdropClick' && handleClose()}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      container={container || document.body}
    >
      <div style={{ width: 1000 }}>
        <Card>
          <CardHeader icon>
            <CardIcon color="warning">
              <CreateIcon />
            </CardIcon>
            <CardIcon
              onClick={handleClose}
              style={{
                float: 'right',
                cursor: 'pointer',
                background: 'darkgray',
                borderRadius: 50,
                boxShadow: '10px 10px 50px gray',
              }}
            >
              <CloseIcon />
            </CardIcon>
          </CardHeader>
          <CardBody>
            <Formik
              initialValues={initialValues}
              onSubmit={values => handleOnSubmit(values)}
              validate={(formObj) => {
                const errors = {};

                Object.entries(formObj).forEach(([key, value]) => {
                  const isEmptyValue = emptyValues.includes(value);
                  if (key === 'group_name' && isEmptyValue) {
                    errors.group_name = translate('validation:RequiredField');
                  } else if (key === 'equipments' && value?.length === 0) {
                    errors[key] = translate('validation:RequiredField');
                  } else if (isEmptyValue) {
                    errors[key] = translate('validation:MustBeGreaterThanZero');
                  }
                });

                return errors;
              }}
              render={({
                values,
                errors,
                touched,
                handleSubmit,
                setValues,
                setTouched,
                resetForm,
              }) => {
                const handleOnChange = (key, value) => {
                  setValues({ ...values, [key]: value });
                  setTouched({ ...touched, [key]: true });
                };

                const hasEmptyFields = Object.values(values).some(v => emptyValues.includes(v));
                const hasErrors = Object.keys(errors).length > 0;
                const hasEmptyEquips = values.equipments?.length === 0;
                const disableSaveButton = hasEmptyFields || hasErrors || hasEmptyEquips;

                return (
                  <form onSubmit={handleSubmit}>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Typography variant={'h2'}>{translate('common:DynamicAllocation')}</Typography>
                    </div>
                    <hr />
                    <div className={classes.equipWrapper}>
                      <div className={classes.labelTypeSelector}>
                        {translate('common:Equipments')}
                      </div>
                      <PF2MAutocomplete
                        multiple
                        isClearable
                        title={''}
                        placeholder={''}
                        options={laOptions}
                        onChange={(_, vals) => {
                          setValues({ ...values, equipments: vals });
                          setTouched({ ...touched, equipments: true });
                        }}
                        value={values?.equipments || []}
                        limitTags={1}
                        textFieldProps={{
                          error: touched.equipments && errors.equipments,
                          helperText: touched.equipments && errors.equipments,
                        }}
                      />
                    </div>
                    <hr />
                    <div style={{
                      padding: '20px 0',
                      flexWrap: 'wrap',
                      maxWidth: '1200px',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '16px 40px',
                    }}
                    >
                      <div className={classes.wrapperField}>
                        {renderLabelWithInfo({
                          description: translate('common:AllocationGroup'),
                          tooltipText: translate('common:AllocationGroupExplanation'),
                        })}
                        <PF2MSearchInput
                          classes={searchFieldClasses}
                          value={values.group_name}
                          onChange={({ target: { value } }) => (value.length <= 2 && handleOnChange('group_name', value))}
                          error={touched.group_name && errors.group_name}
                          helperText={touched.group_name && errors.group_name}
                        />
                      </div>
                      <div className={classes.wrapperField}>
                        {renderLabelWithInfo({
                          description: translate('common:MaxAllocationCapacity'),
                          tooltipText: translate('common:MaxAllocationCapacityExplanation'),
                        })}
                        <PF2MNumericField
                          value={values.max_allocation_capacity}
                          onChange={value => handleOnChange('max_allocation_capacity', value)}
                          error={touched.max_allocation_capacity && errors.max_allocation_capacity}
                          helperText={
                            touched.max_allocation_capacity && errors.max_allocation_capacity
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div className={classes.wrapperField}>
                        {renderLabelWithInfo({
                          description: translate('common:MinAllocationCapacity'),
                          tooltipText: translate('common:MinAllocationCapacityExplanation'),
                        })}
                        <PF2MNumericField
                          value={values.min_allocation_capacity}
                          onChange={value => handleOnChange('min_allocation_capacity', value)}
                          error={
                            touched.min_allocation_capacity
                            && errors.min_allocation_capacity
                          }
                          helperText={
                            touched.min_allocation_capacity
                            && errors.min_allocation_capacity
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div className={classes.wrapperField}>
                        {renderLabelWithInfo({
                          description: translate('common:MaxLoadQueueTime'),
                          tooltipText: translate('common:MaxLoadQueueTimeExplanation'),
                        })}
                        <PF2MNumericField
                          value={values.max_load_queue_time}
                          onChange={value => handleOnChange('max_load_queue_time', value)}
                          error={touched.max_load_queue_time && errors.max_load_queue_time}
                          helperText={touched.max_load_queue_time && errors.max_load_queue_time}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div className={classes.wrapperField}>
                        {renderLabelWithInfo({
                          description: translate('common:MeanQueueTimeRange'),
                          tooltipText: translate('common:MeanQueueTimeRangeExplanation'),
                        })}
                        <PF2MNumericField
                          value={values.mean_queue_time_range}
                          onChange={value => handleOnChange('mean_queue_time_range', value)}
                          error={touched.mean_queue_time_range && errors.mean_queue_time_range}
                          helperText={touched.mean_queue_time_range && errors.mean_queue_time_range}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                    <div className={classes.wrapperActions}>
                      <PF2MGenericButton
                        onClick={() => resetForm}
                        className={classes.buttonActions}
                        startIcon={<DeleteOutlined fontSize="small" />}
                      >
                        {translate('common:DiscardChanges')}
                      </PF2MGenericButton>
                      <PF2MGenericButton
                        variant={'contained'}
                        color={'secondary'}
                        type={'submit'}
                        disabled={disableSaveButton}
                        className={classes.buttonActions}
                        startIcon={<CheckCircleOutlineOutlined fontSize="small" />}
                      >
                        {translate('common:Commit Changes')}
                      </PF2MGenericButton>
                    </div>
                  </form>
                );
              }}
            />
          </CardBody>
        </Card>
      </div>
    </Modal>
  );
};

BulkDynamicAllocationModal.propTypes = {
  classes: PropTypes.object.isRequired,
  isOpen: PropTypes.bool,
  close: PropTypes.func,
  container: PropTypes.element,
};

BulkDynamicAllocationModal.defaultProps = {
  isOpen: false,
  close: () => null,
  container: null,
};

export default withStyles(styles)(BulkDynamicAllocationModal);
